import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ApiError, errorText } from "@common/api";
import { getMe, getProfile, putProfile, queryKeys } from "../api/salvagers";
import RequireAuth from "../components/RequireAuth";
import type { AdminMode } from "../hooks/useAdminMode";
import type { Session } from "../hooks/useSession";

function Account({ adminMode }: { adminMode: AdminMode }) {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: queryKeys.me, queryFn: getMe });
  const profile = useQuery({
    queryKey: queryKeys.profile,
    queryFn: getProfile,
    // 404 means no profile yet, which is not an error worth retrying.
    retry: false,
  });
  const [displayName, setDisplayName] = useState("");
  const save = useMutation({
    mutationFn: putProfile,
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.profile, saved);
      queryClient.invalidateQueries({ queryKey: queryKeys.tables });
      setDisplayName("");
    },
  });

  const noProfileYet = profile.error instanceof ApiError && profile.error.status === 404;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (displayName.trim()) save.mutate(displayName.trim());
  };

  return (
    <>
      {me.error && <p className="notice error">{errorText(me.error, "Could not load your account.")}</p>}
      {me.data && (
        <dl>
          <dt>Email</dt>
          <dd>{me.data.email}</dd>
          <dt>Groups</dt>
          <dd>{me.data.groups.length ? me.data.groups.join(", ") : "none"}</dd>
        </dl>
      )}
      <h2>Profile</h2>
      {profile.data && (
        <p>
          Display name: <strong>{profile.data.display_name}</strong>
        </p>
      )}
      {noProfileYet && <p className="muted">No profile yet. Pick a display name.</p>}
      {profile.error && !noProfileYet && (
        <p className="notice error">{errorText(profile.error, "Could not load your profile.")}</p>
      )}
      <form onSubmit={submit} className="row">
        <input
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder="Display name"
          maxLength={40}
          aria-label="Display name"
        />
        <button className="btn primary" type="submit" disabled={save.isPending || !displayName.trim()}>
          Save
        </button>
      </form>
      {save.error && <p className="notice error">{errorText(save.error, "Could not save.")}</p>}
      {adminMode.available && (
        <>
          <h2>Admin</h2>
          <label className="check">
            <input
              type="checkbox"
              checked={adminMode.on}
              onChange={(event) => adminMode.setOn(event.target.checked)}
            />
            Admin tools
          </label>
          <p className="muted small">Shows the Admin page and the diagnostic logs. Off, you see the site as a player does.</p>
        </>
      )}
    </>
  );
}

export default function AccountPage({ session, adminMode }: { session: Session; adminMode: AdminMode }) {
  return (
    <main className="page">
      <h1>Account</h1>
      <RequireAuth user={session.user} onSignIn={session.signIn}>
        <Account adminMode={adminMode} />
      </RequireAuth>
    </main>
  );
}
